import { useEffect, useMemo, useRef } from 'react';
import type { Dispatch } from 'react';
import type { UIAction } from '../types/state';
import type { useChat } from './useChat';
import type { useThread } from './useThread';
import type { useSessionHistory } from './useSessionHistory';

interface ConversationBindings {
  chat: Pick<ReturnType<typeof useChat>, 'discardActiveSend' | 'setMessages' | 'dispatch'>;
  thread: Pick<ReturnType<typeof useThread>, 'setThreadId'>;
  history: Pick<ReturnType<typeof useSessionHistory>, 'createNewSession'> & { active: boolean };
  uiDispatch: Dispatch<UIAction>;
}

/**
 * One synchronous boundary for conversation changes. Bind the current hook handles during
 * render; stable bridge methods let thread, chat and history initialize without cyclic refs.
 * Subscribers dispose work (such as delayed idle cleanup) before the next conversation starts.
 */
export function useConversationController() {
  const bindings = useRef<ConversationBindings | null>(null);
  const mounted = useRef(true);
  const listeners = useRef(new Set<() => void>());

  useEffect(() => {
    mounted.current = true;
    const subscribers = listeners.current;
    return () => {
      mounted.current = false;
      subscribers.clear();
    };
  }, []);

  return useMemo(() => {
    const invalidate = () => {
      if (!mounted.current) return;
      listeners.current.forEach(dispose => dispose());
      bindings.current?.chat.discardActiveSend();
    };
    const reset = ({ preserveHistory = false, resetThread = true } = {}) => {
      const current = bindings.current;
      if (!mounted.current || !current) return;
      if (preserveHistory && current.history.active) {
        // createNewSession invokes invalidate through its onBeforeChange callback.
        current.history.createNewSession(undefined, resetThread);
        return;
      }
      invalidate();
      current.chat.setMessages([]);
      current.chat.dispatch({ type: 'SET_INPUT', input: '' });
      current.uiDispatch({ type: 'CLEAR_ATTACHMENTS' });
      current.uiDispatch({ type: 'CLEAR_ATTACHMENT_ERRORS' });
      if (resetThread) current.thread.setThreadId(null);
    };
    return {
      bind: (current: ConversationBindings) => {
        bindings.current = current;
      },
      invalidate,
      reset,
      startNew: () => reset({ preserveHistory: true }),
      replace: (preserveHistory = false) => reset({ preserveHistory, resetThread: false }),
      setMessages: (messages => {
        if (mounted.current) bindings.current?.chat.setMessages(messages);
      }) as ConversationBindings['chat']['setMessages'],
      subscribe: (dispose: () => void) => {
        listeners.current.add(dispose);
        return () => {
          listeners.current.delete(dispose);
        };
      },
    };
  }, []);
}
